import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { invoiceService } from '../../services/invoiceService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Trash2, Save, Send, Download, X } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import StripePaymentSection from './components/StripePaymentSection';

export default function InvoiceBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const invoiceId = searchParams?.get('id');
  const isViewMode = searchParams?.get('view') === 'true';
  const isDuplicate = searchParams?.get('duplicate');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [invoice, setInvoice] = useState({
    invoiceNumber: '',
    customerId: '',
    invoiceDate: new Date()?.toISOString()?.split('T')?.[0],
    dueDate: '',
    paymentTerms: 'net_30',
    customTerms: '',
    taxRate: 10,
    discountType: 'percentage',
    discountValue: 0,
    shippingAmount: 0,
    notes: '',
    status: 'draft',
    items: []
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Check authentication
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    // Only load data if user is authenticated
    if (user) {
      loadCustomers();
      if (invoiceId) {
        loadInvoice(invoiceId);
      } else if (isDuplicate) {
        loadInvoiceForDuplication(isDuplicate);
      } else {
        generateInvoiceNumber();
      }
    }
  }, [invoiceId, isDuplicate, user]);

  useEffect(() => {
    calculateTotals();
  }, [invoice?.items, invoice?.taxRate, invoice?.discountType, invoice?.discountValue, invoice?.shippingAmount]);

  const loadCustomers = async () => {
    try {
      const { data, error } = await supabase?.from('customers')?.select('id, company_name, email, phone')?.eq('is_active', true)?.order('company_name');

      if (error) throw error;
      setCustomers(data || []);
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  const loadInvoice = async (id) => {
    try {
      setLoading(true);
      const data = await invoiceService?.getById(id);
      setInvoice({
        invoiceNumber: data?.invoiceNumber,
        customerId: data?.customerId,
        invoiceDate: data?.invoiceDate,
        dueDate: data?.dueDate,
        paymentTerms: data?.paymentTerms,
        customTerms: data?.customTerms || '',
        taxRate: data?.taxRate,
        discountType: data?.discountType || 'percentage',
        discountValue: data?.discountValue || 0,
        shippingAmount: data?.shippingAmount || 0,
        notes: data?.notes || '',
        status: data?.status,
        items: data?.items || []
      });
      setSelectedCustomer(data?.customer);
    } catch (err) {
      setError(err?.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoiceForDuplication = async (id) => {
    try {
      setLoading(true);
      const data = await invoiceService?.getById(id);
      const nextNumber = await invoiceService?.getNextInvoiceNumber();
      
      setInvoice({
        invoiceNumber: nextNumber,
        customerId: data?.customerId,
        invoiceDate: new Date()?.toISOString()?.split('T')?.[0],
        dueDate: '',
        paymentTerms: data?.paymentTerms,
        customTerms: data?.customTerms || '',
        taxRate: data?.taxRate,
        discountType: data?.discountType || 'percentage',
        discountValue: data?.discountValue || 0,
        shippingAmount: data?.shippingAmount || 0,
        notes: data?.notes || '',
        status: 'draft',
        items: data?.items || []
      });
      setSelectedCustomer(data?.customer);
    } catch (err) {
      setError(err?.message || 'Failed to load invoice for duplication');
    } finally {
      setLoading(false);
    }
  };

  const generateInvoiceNumber = async () => {
    try {
      const number = await invoiceService?.getNextInvoiceNumber();
      setInvoice(prev => ({ ...prev, invoiceNumber: number }));
    } catch (err) {
      console.error('Error generating invoice number:', err);
    }
  };

  const handleCustomerChange = (customerId) => {
    setInvoice(prev => ({ ...prev, customerId }));
    const customer = customers?.find(c => c?.id === customerId);
    setSelectedCustomer(customer);
  };

  const handlePaymentTermsChange = (terms) => {
    setInvoice(prev => ({ ...prev, paymentTerms: terms }));
    
    if (terms !== 'custom' && invoice?.invoiceDate) {
      const daysMap = {
        due_on_receipt: 0,
        net_15: 15,
        net_30: 30,
        net_60: 60
      };
      
      const days = daysMap?.[terms] || 30;
      const dueDate = new Date(invoice.invoiceDate);
      dueDate?.setDate(dueDate?.getDate() + days);
      setInvoice(prev => ({ ...prev, dueDate: dueDate?.toISOString()?.split('T')?.[0] }));
    }
  };

  const addItem = () => {
    setInvoice(prev => ({
      ...prev,
      items: [
        ...prev?.items,
        {
          id: `temp-${Date.now()}`,
          description: '',
          quantity: 1,
          unitPrice: 0,
          totalPrice: 0
        }
      ]
    }));
  };

  const updateItem = (index, field, value) => {
    setInvoice(prev => {
      const items = [...prev?.items];
      items[index] = { ...items?.[index], [field]: value };
      
      // Calculate total price for the item
      if (field === 'quantity' || field === 'unitPrice') {
        const quantity = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(items?.[index]?.quantity) || 0;
        const unitPrice = field === 'unitPrice' ? parseFloat(value) || 0 : parseFloat(items?.[index]?.unitPrice) || 0;
        items[index].totalPrice = quantity * unitPrice;
      }
      
      return { ...prev, items };
    });
  };

  const removeItem = (index) => {
    setInvoice(prev => ({
      ...prev,
      items: prev?.items?.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    const subtotal = invoice?.items?.reduce((sum, item) => sum + (parseFloat(item?.totalPrice) || 0), 0);
    const taxAmount = (subtotal * (parseFloat(invoice?.taxRate) || 0)) / 100;
    
    let discountAmount = 0;
    if (invoice?.discountType === 'percentage') {
      discountAmount = (subtotal * (parseFloat(invoice?.discountValue) || 0)) / 100;
    } else {
      discountAmount = parseFloat(invoice?.discountValue) || 0;
    }
    
    const shippingAmount = parseFloat(invoice?.shippingAmount) || 0;
    const total = subtotal + taxAmount - discountAmount + shippingAmount;
    
    return {
      subtotal: subtotal?.toFixed(2),
      taxAmount: taxAmount?.toFixed(2),
      discountAmount: discountAmount?.toFixed(2),
      shippingAmount: shippingAmount?.toFixed(2),
      total: total?.toFixed(2)
    };
  };

  const handleSave = async (shouldSend = false) => {
    try {
      setLoading(true);
      setError('');

      if (!invoice?.customerId) {
        throw new Error('Please select a customer');
      }

      if (!invoice?.dueDate) {
        throw new Error('Please set a due date');
      }

      if (invoice?.items?.length === 0) {
        throw new Error('Please add at least one line item');
      }

      const totals = calculateTotals();
      const invoiceData = {
        ...invoice,
        subtotal: parseFloat(totals?.subtotal),
        taxAmount: parseFloat(totals?.taxAmount),
        discountAmount: parseFloat(totals?.discountAmount),
        shippingAmount: parseFloat(totals?.shippingAmount),
        totalAmount: parseFloat(totals?.total),
        status: shouldSend ? 'sent' : invoice?.status
      };

      if (invoiceId && !isDuplicate) {
        await invoiceService?.update(invoiceId, invoiceData);
      } else {
        await invoiceService?.create(invoiceData);
      }

      navigate('/invoice-list');
    } catch (err) {
      setError(err?.message || 'Failed to save invoice');
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const totals = calculateTotals();

    // Company header
    doc?.setFontSize(20);
    doc?.text('INVOICE', 20, 20);
    
    doc?.setFontSize(10);
    doc?.text(`Invoice #: ${invoice?.invoiceNumber}`, 20, 30);
    doc?.text(`Date: ${new Date(invoice.invoiceDate)?.toLocaleDateString()}`, 20, 35);
    doc?.text(`Due Date: ${new Date(invoice.dueDate)?.toLocaleDateString()}`, 20, 40);

    // Customer info
    if (selectedCustomer) {
      doc?.setFontSize(12);
      doc?.text('Bill To:', 120, 30);
      doc?.setFontSize(10);
      doc?.text(selectedCustomer?.company_name || '', 120, 35);
      doc?.text(selectedCustomer?.email || '', 120, 40);
      doc?.text(selectedCustomer?.phone || '', 120, 45);
    }

    // Line items table
    const tableData = invoice?.items?.map(item => [
      item?.description,
      item?.quantity?.toString(),
      `$${parseFloat(item?.unitPrice)?.toFixed(2)}`,
      `$${parseFloat(item?.totalPrice)?.toFixed(2)}`
    ]);

    doc?.autoTable({
      startY: 60,
      head: [['Description', 'Quantity', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] }
    });

    // Totals
    const finalY = doc?.lastAutoTable?.finalY || 60;
    doc?.text(`Subtotal: $${totals?.subtotal}`, 140, finalY + 10);
    doc?.text(`Tax (${invoice?.taxRate}%): $${totals?.taxAmount}`, 140, finalY + 15);
    if (parseFloat(totals?.discountAmount) > 0) {
      doc?.text(`Discount: -$${totals?.discountAmount}`, 140, finalY + 20);
    }
    if (parseFloat(totals?.shippingAmount) > 0) {
      doc?.text(`Shipping: $${totals?.shippingAmount}`, 140, finalY + 25);
    }
    
    doc?.setFontSize(12);
    doc?.setFont(undefined, 'bold');
    doc?.text(`Total: $${totals?.total}`, 140, finalY + 35);

    // Notes
    if (invoice?.notes) {
      doc?.setFontSize(10);
      doc?.setFont(undefined, 'normal');
      doc?.text('Notes:', 20, finalY + 50);
      doc?.text(invoice?.notes, 20, finalY + 55, { maxWidth: 170 });
    }

    doc?.save(`invoice-${invoice?.invoiceNumber}.pdf`);
  };

  const handlePaymentSuccess = (paymentResult) => {
    // Handle successful payment
    setInvoice(prev => ({ ...prev, status: 'paid' }));
    setError('');
    // You can add additional logic here like updating the invoice status in the database
  };

  const totals = calculateTotals();

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if not authenticated (will redirect)
  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isViewMode ? 'View Invoice' : invoiceId && !isDuplicate ? 'Edit Invoice' : 'Create Invoice'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isViewMode ? 'Invoice details' : 'Fill in the details below to create or edit an invoice'}
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/invoice-list')}>
            <X size={20} className="mr-2" />
            Close
          </Button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Form */}
          <div className="space-y-6">
            {/* Invoice Details */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Number *
                  </label>
                  <Input
                    type="text"
                    value={invoice?.invoiceNumber}
                    onChange={(e) => setInvoice({ ...invoice, invoiceNumber: e?.target?.value })}
                    disabled={isViewMode}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer *
                  </label>
                  <Select
                    value={invoice?.customerId}
                    onChange={(e) => handleCustomerChange(e?.target?.value)}
                    disabled={isViewMode}
                    required
                  >
                    <option value="">Select a customer</option>
                    {customers?.map(customer => (
                      <option key={customer?.id} value={customer?.id}>
                        {customer?.company_name}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Invoice Date *
                    </label>
                    <Input
                      type="date"
                      value={invoice?.invoiceDate}
                      onChange={(e) => setInvoice({ ...invoice, invoiceDate: e?.target?.value })}
                      disabled={isViewMode}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Due Date *
                    </label>
                    <Input
                      type="date"
                      value={invoice?.dueDate}
                      onChange={(e) => setInvoice({ ...invoice, dueDate: e?.target?.value })}
                      disabled={isViewMode}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Terms
                  </label>
                  <Select
                    value={invoice?.paymentTerms}
                    onChange={(e) => handlePaymentTermsChange(e?.target?.value)}
                    disabled={isViewMode}
                  >
                    <option value="due_on_receipt">Due on Receipt</option>
                    <option value="net_15">Net 15</option>
                    <option value="net_30">Net 30</option>
                    <option value="net_60">Net 60</option>
                    <option value="custom">Custom</option>
                  </Select>
                </div>

                {invoice?.paymentTerms === 'custom' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Terms
                    </label>
                    <Input
                      type="text"
                      value={invoice?.customTerms}
                      onChange={(e) => setInvoice({ ...invoice, customTerms: e?.target?.value })}
                      disabled={isViewMode}
                      placeholder="Enter custom payment terms"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Line Items */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Line Items</h2>
                {!isViewMode && (
                  <Button size="sm" onClick={addItem}>
                    <Plus size={16} className="mr-1" />
                    Add Item
                  </Button>
                )}
              </div>

              <div className="space-y-4">
                {invoice?.items?.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No items added yet</p>
                ) : (
                  invoice?.items?.map((item, index) => (
                    <div key={item?.id || index} className="border rounded-lg p-4">
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description *
                          </label>
                          <Input
                            type="text"
                            value={item?.description}
                            onChange={(e) => updateItem(index, 'description', e?.target?.value)}
                            disabled={isViewMode}
                            placeholder="Item description"
                          />
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Quantity *
                            </label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item?.quantity}
                              onChange={(e) => updateItem(index, 'quantity', e?.target?.value)}
                              disabled={isViewMode}
                              min="0"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Unit Price *
                            </label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item?.unitPrice}
                              onChange={(e) => updateItem(index, 'unitPrice', e?.target?.value)}
                              disabled={isViewMode}
                              min="0"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Total
                            </label>
                            <Input
                              type="number"
                              value={item?.totalPrice?.toFixed(2) || '0.00'}
                              disabled
                            />
                          </div>
                        </div>

                        {!isViewMode && (
                          <div className="flex justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => removeItem(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={16} className="mr-1" />
                              Remove
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Tax and Discount */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Tax & Adjustments</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tax Rate (%)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={invoice?.taxRate}
                    onChange={(e) => setInvoice({ ...invoice, taxRate: parseFloat(e?.target?.value) || 0 })}
                    disabled={isViewMode}
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Type
                  </label>
                  <Select
                    value={invoice?.discountType}
                    onChange={(e) => setInvoice({ ...invoice, discountType: e?.target?.value })}
                    disabled={isViewMode}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount {invoice?.discountType === 'percentage' ? '(%)' : '($)'}
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={invoice?.discountValue}
                    onChange={(e) => setInvoice({ ...invoice, discountValue: parseFloat(e?.target?.value) || 0 })}
                    disabled={isViewMode}
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shipping ($)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={invoice?.shippingAmount}
                    onChange={(e) => setInvoice({ ...invoice, shippingAmount: parseFloat(e?.target?.value) || 0 })}
                    disabled={isViewMode}
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="4"
                value={invoice?.notes}
                onChange={(e) => setInvoice({ ...invoice, notes: e?.target?.value })}
                disabled={isViewMode}
                placeholder="Additional notes or terms..."
              />
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-6">
            {/* Preview */}
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Preview</h2>
              
              <div className="border rounded-lg p-6 bg-gray-50">
                {/* Header */}
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">INVOICE</h3>
                  <div className="mt-2 text-sm text-gray-600">
                    <div>Invoice #: {invoice?.invoiceNumber}</div>
                    <div>Date: {invoice?.invoiceDate ? new Date(invoice.invoiceDate)?.toLocaleDateString() : '-'}</div>
                    <div>Due: {invoice?.dueDate ? new Date(invoice.dueDate)?.toLocaleDateString() : '-'}</div>
                  </div>
                </div>

                {/* Customer Info */}
                {selectedCustomer && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Bill To:</h4>
                    <div className="text-sm text-gray-600">
                      <div className="font-medium">{selectedCustomer?.company_name}</div>
                      <div>{selectedCustomer?.email}</div>
                      <div>{selectedCustomer?.phone}</div>
                    </div>
                  </div>
                )}

                {/* Items */}
                <div className="mb-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 text-gray-700">Item</th>
                        <th className="text-right py-2 text-gray-700">Qty</th>
                        <th className="text-right py-2 text-gray-700">Price</th>
                        <th className="text-right py-2 text-gray-700">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice?.items?.map((item, index) => (
                        <tr key={index} className="border-b">
                          <td className="py-2 text-gray-600">{item?.description || 'Item'}</td>
                          <td className="text-right py-2 text-gray-600">{item?.quantity}</td>
                          <td className="text-right py-2 text-gray-600">${parseFloat(item?.unitPrice || 0)?.toFixed(2)}</td>
                          <td className="text-right py-2 text-gray-600">${parseFloat(item?.totalPrice || 0)?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="border-t pt-4">
                  <div className="flex justify-between mb-2 text-sm">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="text-gray-900">${totals?.subtotal}</span>
                  </div>
                  <div className="flex justify-between mb-2 text-sm">
                    <span className="text-gray-600">Tax ({invoice?.taxRate}%):</span>
                    <span className="text-gray-900">${totals?.taxAmount}</span>
                  </div>
                  {parseFloat(totals?.discountAmount) > 0 && (
                    <div className="flex justify-between mb-2 text-sm">
                      <span className="text-gray-600">Discount:</span>
                      <span className="text-red-600">-${totals?.discountAmount}</span>
                    </div>
                  )}
                  {parseFloat(totals?.shippingAmount) > 0 && (
                    <div className="flex justify-between mb-2 text-sm">
                      <span className="text-gray-600">Shipping:</span>
                      <span className="text-gray-900">${totals?.shippingAmount}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t font-semibold">
                    <span className="text-gray-900">Total:</span>
                    <span className="text-gray-900 text-lg">${totals?.total}</span>
                  </div>
                </div>

                {/* Notes */}
                {invoice?.notes && (
                  <div className="mt-6 text-sm">
                    <h4 className="font-semibold text-gray-700 mb-2">Notes:</h4>
                    <p className="text-gray-600 whitespace-pre-wrap">{invoice?.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Stripe Payment Section - NEW */}
            {!isViewMode && invoice?.status !== 'paid' && (
              <StripePaymentSection 
                invoice={invoice} 
                onPaymentSuccess={handlePaymentSuccess}
              />
            )}

            {/* Actions */}
            {!isViewMode && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
                <div className="space-y-3">
                  <Button
                    onClick={() => handleSave(false)}
                    className="w-full flex items-center justify-center gap-2"
                    disabled={loading}
                  >
                    <Save size={20} />
                    Save as Draft
                  </Button>
                  
                  <Button
                    onClick={() => handleSave(true)}
                    className="w-full flex items-center justify-center gap-2"
                    disabled={loading}
                  >
                    <Send size={20} />
                    Save & Send
                  </Button>
                  
                  <Button
                    onClick={generatePDF}
                    variant="outline"
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <Download size={20} />
                    Download PDF
                  </Button>
                </div>
              </div>
            )}

            {isViewMode && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="space-y-3">
                  <Button
                    onClick={generatePDF}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <Download size={20} />
                    Download PDF
                  </Button>
                  
                  <Button
                    onClick={() => navigate(`/invoice-builder?id=${invoiceId}`)}
                    variant="outline"
                    className="w-full"
                  >
                    Edit Invoice
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}